import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { PageHeader, LinkButton, Table, StatusBadge, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { formatDate } from "@/lib/format";

export default function AdminCmsList() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["cms-list"], queryFn: async () => (await api.get("/admin/cms-pages")).data });
  const { sort, toggleSort, sortRows } = useTableSort();
  const sorted = sortRows(data, (p, key) => {
    if (key === "is_public") return p.is_public ? 1 : 0;
    if (key === "updated_at") return p.updated_at ? new Date(p.updated_at).getTime() : null;
    return p[key] ?? null;
  });

  async function remove(p) {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/cms-pages/${p.id}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["cms-list"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div>
      <PageHeader title="CMS pages" subtitle={`${data.length} pages`} actions={<LinkButton to="/admin/cms-pages/new">+ New page</LinkButton>} />
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="Title" sortKey="title" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Slug" sortKey="slug" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Public" sortKey="is_public" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Updated" sortKey="updated_at" sort={sort} onSort={toggleSort} />
              <SortableHeader label="" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.id} className="border-b border-[#E9ECEF]">
                <td className="px-3 py-3 font-medium">{p.title}</td>
                <td className="px-3 py-3 font-mono text-xs">/{p.slug}</td>
                <td className="px-3 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-3 py-3">{p.is_public ? "Yes" : "No"}</td>
                <td className="px-3 py-3 text-xs">{formatDate(p.updated_at)}</td>
                <td className="px-3 py-3 text-sm">
                  <Link to={`/admin/cms-pages/${p.id}`} className="underline mr-3">Edit</Link>
                  <button
                    onClick={() => remove(p)}
                    className="text-red-600 underline hover:text-red-700"
                    data-testid={`cms-delete-${p.slug}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
